import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function CommercialReadiness({ buyerId, onReview }) {
  const [review, setReview] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const [form, setForm] = useState({ acquisition_evidence_reference: "",
    fulfillment_evidence_reference: "", approved_quantity: 5,
    acquisition_pilot_verified: false, fulfillment_capacity_verified: false });
  useEffect(() => {
    let active = true;
    setReview(null); setError(""); onReview(null);
    api.get(`/platform/lead-buyers/${buyerId}/commercial-review`).then(({data}) => {
      if (active && data?.id) { setReview(data); onReview(data); }
    }).catch(() => {});
    return () => { active = false; };
  }, [buyerId, onReview]);
  const update = (key, value) => setForm(old => ({...old, [key]: value}));
  const submit = async event => {
    event.preventDefault(); if (busy) return;
    setBusy(true); setError("");
    try {
      const {data} = await api.post(`/platform/lead-buyers/${buyerId}/commercial-review`, {
        ...form, approved_quantity: Number(form.approved_quantity) });
      if (alive.current) { setReview(data); onReview(data); }
    } catch(e) { if (alive.current) setError(formatApiErrorDetail(e?.response?.data?.detail) || "Evidence review could not be saved."); }
    finally { if (alive.current) setBusy(false); }
  };
  if (review) return <section className="space-y-2 rounded-md border p-3 text-sm" data-testid="commercial-review">
    <strong>Commercial evidence review recorded</strong>
    <p>{review.remaining_capacity} leads of reviewed order capacity remain. Expires {new Date(review.expires_at).toLocaleString()}.</p>
    <p className="text-xs text-muted-foreground">Owner-reviewed evidence. This is not automated provider verification. Existing obligations must be reconciled before renewal.</p>
  </section>;
  return <form className="space-y-3 rounded-md border p-3 text-sm" onSubmit={submit}>
    <h3 className="font-semibold">Verify acquisition and fulfillment before sales</h3>
    <p>Orders and checkout stay blocked until you review real acquisition results and the capacity to fulfill this buyer's orders. Synthetic tests alone do not establish readiness.</p>
    <label className="block">Real acquisition evidence reference<input required minLength={12} maxLength={300}
      className="mt-1 block w-full rounded-md border bg-background p-2" value={form.acquisition_evidence_reference}
      onChange={e=>update("acquisition_evidence_reference",e.target.value)} /></label>
    <label className="block">Fulfillment capacity evidence reference<input required minLength={12} maxLength={300}
      className="mt-1 block w-full rounded-md border bg-background p-2" value={form.fulfillment_evidence_reference}
      onChange={e=>update("fulfillment_evidence_reference",e.target.value)} /></label>
    <label className="block">Maximum approved order quantity<input required type="number" min={1} max={100}
      className="mt-1 block w-full rounded-md border bg-background p-2" value={form.approved_quantity}
      onChange={e=>update("approved_quantity",e.target.value)} /></label>
    <label className="flex items-start gap-2"><input type="checkbox" required checked={form.acquisition_pilot_verified}
      onChange={e=>update("acquisition_pilot_verified",e.target.checked)} />I reviewed reliable real lead acquisition evidence.</label>
    <label className="flex items-start gap-2"><input type="checkbox" required checked={form.fulfillment_capacity_verified}
      onChange={e=>update("fulfillment_capacity_verified",e.target.checked)} />I verified capacity to fulfill the approved quantity.</label>
    {error && <p role="alert">{error}</p>}
    <button className="rounded-md border px-3 py-2 disabled:opacity-40" disabled={busy || !form.acquisition_pilot_verified || !form.fulfillment_capacity_verified}>
      Record commercial evidence review
    </button>
  </form>;
}
